import uuid from 'uuid/v4';
import * as DeviceConst from './device_const';
import Logger from './logger';
import Store from './store';

/**
 * Device detection util
 *
 * Helper for getting data regarding the current device such as os, browser touch ability etc.
 * Most of the elements are pre-processed and are not functions and can be accessed directly
 *
 * Object is a singleton
 *
 *
 */
class Device {
    public logger: Logger;
    private _minWidthForPC: number = 1280;
    private _widthForLowResolutionPC: number = 1480;
    private static _clientHints: DeviceConst.ClientHints;

    /**
     * Initialize the device.
     *
     * @constructor
     */
    constructor() {
        this.logger = new Logger((this.constructor as any).CLASS);

        Device._getClientHints().then((res) => {
            if (res) {
                Device._clientHints = {} as DeviceConst.ClientHints;
                Object.assign(Device._clientHints, res);
            }
        }, () => { });
    }

    private static async _getClientHints(): Promise<DeviceConst.ClientHints> {
        if (window.navigator["getUserAgent"] !== undefined) {
            return await window.navigator["getUserAgent"]();
        }

        return Promise.reject("navigator.getUserAgent API not available");
    }

    public get clientHints(): DeviceConst.ClientHints {
        return Device._clientHints;
    }

    /**
     * Get device window width.
     *
     * @returns {Number}
     */
    public getWidth(): number {
        return window.innerWidth;
    }


    /**
     * Get device window height.
     *
     * @returns {Number}
     */
    public getHeight(): number {
        return window.innerHeight;
    }


    /**
     * Getter for the user-agent for the client being handled in this request.
     *
     * @example 'Mozilla/5.0 (iPad; CPU OS 7_0 like Mac OS X) AppleWebKit/537.51.1 (KHTML, like Gecko) Version/7.0 Mobile/11A465 Safari/9537.53'
     * @returns {string} - current user agent that runs the app
     */
    public getUserAgent(): string {
        return window.navigator.userAgent;
    }

    public getNavigatorData() {
        return window.navigator;
    }

    public getNetworkData():string {
        let connection = navigator["connection"] || navigator["mozConnection"] || navigator["webkitConnection"];
        if(connection) {
            return (connection.effectiveType?connection.effectiveType:'') + '_'
                + (connection.rtt?connection.rtt:'') + '_'
                + (connection.downlink?connection.downlink:'');
        }
        return null;
    }

    /**
     * Used to detected if user clicked and loaded the app using home screen
     *
     * @returns {boolean} - Returns true if the app is running in standalone mode (iOS only)
     */
    public isStandalone(): boolean {
        return 'standalone' in window.navigator ? (window.navigator as any).standalone : false;
    }

    /**
     * Generates a GUID
     */
    public generateGuid(): string {
        return uuid();
    }

    /**
     * Used to detected if user clicked and loaded the app using home screen
     *
     * @returns {boolean} - Returns true if the app is running in standalone mode (iOS only)
     */
    public getDeviceTypeName(): string {

        const hybridTypeByDevice = () => {
            if (this.isIphone()) {
                return DeviceConst.DEVICE_TYPE.IPHONE_NATIVE;
            } else if (this.isIpad()) {
                return DeviceConst.DEVICE_TYPE.IPAD_NATIVE;
            }

            return DeviceConst.DEVICE_TYPE.OTHER;
        };

        const notHybridTypeByDevice = () => {

            if (this.isIphone()) {
                return DeviceConst.DEVICE_TYPE.IPHONE;
            } else if (this.isIpad()) {
                return DeviceConst.DEVICE_TYPE.IPAD;
            } else if (this.isMobileByOS()) {
                return DeviceConst.DEVICE_TYPE.OTHER;
            }

            return DeviceConst.DEVICE_TYPE.PC
        };

        if (!this.isHybrid()) {
            return notHybridTypeByDevice();
        }

        return hybridTypeByDevice();
    }


    /**
     * Is touch enabled on this device?
     *
     * @returns {boolean} - Returns true if the device supports Touch
     */
    public isTouch(): boolean {
        return 'ontouchstart' in window
            || ((window as any).DocumentTouch && document instanceof (window as any).DocumentTouch);
    }


    /**
     * Is it an iOS?
     * @returns {boolean} - Returns true if the OS is iOS
     * @Note iPad OS 13 and up will return false (is MAC)
     */
    public isIOS(): boolean {
        return (/iP(hone|od|ad)/i).test(this.getUserAgent().toLowerCase());
    }


    /**
     * Is it an Hybrid?
     * @Note With no parameters it will check if Casino Hybrid app only)
     *
     * @returns {boolean} - Returns true if the app is running in Hybrid
     */
    public isHybrid(iosName?: string, androidName?: string): boolean {
        if (this.isIOS()) {
            return this.isNativeIOS(iosName);
        }

        if (this.isAndroid()) {
            return this.isNativeAndroid(androidName);
        }

        return false;
    }


    /**
     * @description Is any brand of Hybrid - check if it is Casino/Poker/Sport Hybrid
     */
    public isTypeOfHybrid(): boolean {
        if (this.isHybrid())
            return true;

        if (this.isPokerHybrid())
            return true;

        if (this.isSportHybrid())
            return true;

        return false;
    }


    /**
     * Detect Is Poker Hybrid
     * @private
     */
    public isPokerHybrid(): boolean {
        let isPokerHybridFlag = false;
        if (this.isIOS()) {
            isPokerHybridFlag =
                (
                    (window as any).NativeInterface
                    && (this.isNativeIOS(DeviceConst.NATIVE_TYPE.POKER_IOS))
                )
                || (
                    ['103', '106'].indexOf(Store.get('sourceSubBrand')) !== -1
                    && Store.get('ClientVersion') && Store.get('ClientVersion').toLowerCase().startsWith('mp-')
                )
                || (
                    ['103', '106'].indexOf(Store.get('sourceSubBrand')) !== -1
                    && Store.get('ClientVersion') && Store.get('ClientVersion').toLowerCase().startsWith('sp-')
                );

        } else if (this.isAndroid()) {
            isPokerHybridFlag =
                (
                    (window as any)[DeviceConst.NATIVE_TYPE.POKER_ANDROID] && this.isNativeAndroid(DeviceConst.NATIVE_TYPE.POKER_ANDROID)
                )
                || (
                    (window as any)[DeviceConst.NATIVE_TYPE.CIP8_ANDROID] && this.isNativeAndroid(DeviceConst.NATIVE_TYPE.CIP8_ANDROID)
                )
                || (
                    ['103', '106'].indexOf(Store.get('sourceSubBrand')) !== -1
                    && (Store.get('sourceProductPackage')  && (
                    Store.get('sourceProductPackage').toString() === '52'
                        ||
                    Store.get('sourceProductPackage').toString() === '95'
                    ))
                );
        }

        return isPokerHybridFlag;
    }


    /**
     * Detect Is Sport Hybrid
     * @private
     */
    public isSportHybrid(): boolean {
        if (this.isIOS()) {
            return this.isNativeIOS(DeviceConst.NATIVE_TYPE.SPORT_IOS_EMBEDDED);
        }

        if (this.isAndroid()) {
            return this.isNativeAndroid(DeviceConst.NATIVE_TYPE.SPORT_ANDROID);
        }

        return false;
    }


    /**
     * @description Is hybrid Ios app
     */
    public isHybridCasinoIos(): boolean {
        return window['cgp_isHybridCasinoIos'] || false;
    }

    /**
     * @description Is hybrid Ios app in poker
     */
    public isHybridPokerIos(): boolean {
        return window['cgp_isHybridPokerIos'] || false;
    }

    /**
     * @description Is hybrid Ios app in sport
     */
    public isHybridSportIos(): boolean {
        return window['cgp_isHybridSportIos'] || false;
    }

    /**
     * @description Is compiled hybrid app
     */
    public isHybridEmbedded(): boolean {
        return window['cgp_isHybridEmbedded'] || false;
    }

    // used for choosing correct "device" section for specific device type in GNWP
    public getDeviceTypeForGNWP(): string {
        if (this.isHybridEmbedded()) {
            return DeviceConst.DEVICE_GNWP_TYPE.HYBRID_IOS_EMBEDDED;
        }
        return "";
    }

    /**
     * ??
     *
     * @returns {number}
     */
    public getNativeType(): DeviceConst.DEVICE_ENUM_TYPE {
        if (this.isHybrid() && this.isIOS()) {
            return DeviceConst.DEVICE_ENUM_TYPE.IOS;
        } else if (this.isHybrid() && this.isAndroid()) {
            return DeviceConst.DEVICE_ENUM_TYPE.ANDROID;
        }

        return DeviceConst.DEVICE_ENUM_TYPE.NON;
    }

    /**
     * Is it an IOS Hybrid?
     *
     * @returns {Boolean}
     */
    public isNativeIOS(name?: string): boolean {
        const className = this._getClassName(name, DeviceConst.NATIVE_TYPE.CASINO_IOS);

        return ('object' === typeof ((window as any).webkit)
            && 'object' === typeof ((window as any).webkit.messageHandlers[className]));
    }

    /**
     * Is it an Android Hybrid?
     *
     * @returns {Boolean}
     */
    public isNativeAndroid(name: string): boolean {
        const className = this._getClassName(name, DeviceConst.NATIVE_TYPE.CASINO_ANDROID);
        return ('object' === typeof ((window as any)[className]));
    }

    /**
     * Is it an Android?
     *
     * @returns {boolean} - Returns true if the OS is Android
     */
    public isAndroid(): boolean {
        return (/android/i).test(this.getUserAgent().toLowerCase());
    }


    /**
     * Is it a Firefox?
     *
     * @returns {boolean} - Returns true if the browser is Firefox
     */
    public isFirefox(): boolean {
        return (/firefox/i).test(this.getUserAgent().toLowerCase());
    }


    /**
     * Is it a Chrome?
     *
     * @returns {boolean} - Returns true if the browser is Chrome
     */
    public isChrome(): boolean {
        return ((/chrome/i).test(this.getUserAgent().toLowerCase()) && (/google/i).test(navigator.vendor.toLowerCase())) || ((/crios/i).test(this.getUserAgent().toLowerCase()) && (/apple/i).test(navigator.vendor.toLowerCase()));
    }


    /**
     * Is it a Safari?
     *
     * @returns {boolean} - Returns true if the browser is Safari
     */
    public isSafari(): boolean {
        return (
                (/safari/i).test(this.getUserAgent().toLowerCase()) ||
                (/iphone/i).test(this.getUserAgent().toLowerCase()) ||
                (/ipad/i).test(this.getUserAgent().toLowerCase()) ||
                (/macintosh/i).test(this.getUserAgent().toLowerCase())
            )
            && (/apple/i).test(navigator.vendor.toLowerCase())
            && !(/crios/i).test(this.getUserAgent().toLowerCase());
    }

    /**
     * Is it a Opera?
     *
     * @returns {boolean} - Returns true if the browser is Opera
     */
    public isOpera(): boolean {
        return (/opera/i).test(this.getUserAgent().toLowerCase());
    }


    /**
     * Is browser an Edge instance?
     *
     * @returns {boolean} - Returns true if the browser is chromium based Edge
     * detection is based on the new Client Hints API, with a fallback on the soon to be obsolete UA, if userAgentData is unsupported
     * or API names change
     */
    public isEdge(): boolean {
        if(navigator["userAgentData"]?.["brands"]?.length)
            for (let brandEntry of navigator["userAgentData"]["brands"]) {
                if (brandEntry.brand?.indexOf("Edge") !== -1) {
                    return true;
                }
            }

        return (/Edg/i).test(this.getUserAgent());
    }

    /**
     * Check if the browser is Samsung Internet (Samsung default browser).
     *
     * @returns {boolean}
     */
    public isSamsungInternet(): boolean {
        return /SAMSUNG|SGH-[I|N|T]|GT-[I|P|N]|SM-[N|P|T|Z|G]|SHV-E|SCH-[I|J|R|S]|SPH-L/i.test(this.getUserAgent().toUpperCase());
    }


    /**
     * Is it a PC?
     *
     * @returns {boolean} - Returns true if it's a PC running me
     */
    public isPC(): boolean {
        return (/windows|linux|MacOS|Macintosh|UNIX/i).test(this.getUserAgent().toLowerCase()) && !this.isMobileByOS();
    }


    /**
     * Is mobile device? Based on OS.
     * Encapsulates the decision
     * //isIos13 is like PC -not mobile
     * @returns {boolean} - Returns true if it's a Mobile (AKA, iOS or Android device)
     */
    public isMobileByOS(): boolean {
        return (this.isIOS() || this.isAndroid());
    }


    /**
     * Is mobile device? Based on Width smaller than 768
     * Encapsulates the decision
     *
     * @returns {boolean} - Returns true if it's a Mobile (AKA, small screen)
     */
    public isMobileByWidth(): boolean {
        return this.getWidth() < this._minWidthForPC;
    }


    /**
     * Is mobile device? Based on all conditions, OS or width
     *
     * @returns {boolean} - Returns true if it's a Mobile
     */
    public isMobile(): boolean {
        return this.isMobileByWidth();
    }


    /**
     * Check if this is PC in low resolution
     *
     * @returns {boolean} - Returns true if it's a Mobile
     */
    public isPCLowResolution(): boolean {
        return this.isPC() && this.getWidth() < this._widthForLowResolutionPC;
    }


    /**
     * Is mobile and not tablet device
     * @returns {boolean}
     */
    public isMobileNotTablet(): boolean {
        return this.isMobile() && !this.isTablet();
    }


    /**
     * Is mobile device? Based on all conditions, OS or width
     *
     * @returns {boolean} - Returns true if it's a Mobile
     */
    public isTablet(): boolean {
        return this.isIpad() && this.isMobile();
    }

    /**
     * Check the device is in a portrait mode.
     *
     * @returns {boolean} - Returns true if it's a Mobile (AKA, small screen)
     */
    public isPortrait(): boolean {
        if (this.isMobile() && this.isAndroid()) {
            // fix for Samsung S8 series (S8-S9...).
            // In full screen mode window.screen.width and window.screen.height after single rotation get wrong values (width equal to height)
            // So we should test it by innerWidth and innerHeight like in iOS
            if (this.getScreenWidth() === this.getScreenHeight()) {
                return this.getWidth() < this.getHeight();
            }
            return this.getScreenWidth() < this.getScreenHeight();
        } else {
            return this.getWidth() < this.getHeight();
        }
    }

    /**
     * Getting the current running iOS version, if detected.
     * Will return false if not an iOS device
     *
     * @returns {*} - Returns null if NOT iOS. If is iOS returns an Number of the current version
     */
    public getIOSVersion(): number {
        const agent = this.getUserAgent();
        const start = agent.indexOf('OS ');

        if (this.isIOS()) {
            return Number(agent.substr(start + 3, 3).replace('_', '.'));
        }

        return null;
    }

    /**
     * Is it an iPhone?
     *
     * @returns {boolean} - Returns true if the device is iPhone
     */
    public isIphone(): boolean {
        return (/(iphone)/i).test(this.getUserAgent());
    }


    /**
     * Is it an iPod?
     *
     * @returns {boolean} - Returns true if the device is iPod
     */
    public isIpod(): boolean {
        return (/(ipod)/i).test(this.getUserAgent());
    }


    /***
     * Is it an iPad?
     * @Description This is the only method that will detect iPad devices with version 13 and up ad iPad
     * @returns {boolean} - Returns true if the device is iPad
     * @Note Should be use only in rare cases (for example force GRS(GGP games) to return the mobile version on iPad)
     */
    public isIpadRealDevice(): boolean {
        // Pre iOS 13 detection
        if (this.isIpad()) {
            return true;

        } else {
            // Post iOS 13 detection
            // iPad with iOS 13 will return "MacIntel" as the platform
            // iPhone with iOS 13 will still return "iPhone" as the platform
            // So if it's not an iPhone/iPod but the OS is iOS it can only be iPad (hopefully)
            // Added the check for "Win32" so the Chrome DevTools don't throw a false positive...

            const navigatorData = this.getNavigatorData();
            // iOS 13+ will declare MacIntel
            const platformName = navigatorData.platform;

            // Is undefined in Safari desktop and mobile below iOS 13 (Chrome desktop actually has it defined but as 0)
            const maxTouchPoints = navigatorData.maxTouchPoints !== undefined ? navigatorData.maxTouchPoints : 0;
            return !this.isIphone() &&
                !this.isIpod() &&
                !(/Win32/i).test(platformName) &&
                (platformName === "MacIntel" && maxTouchPoints > 1);
        }
    }


    isIpad() {
        return (/(ipad)/i).test(this.getUserAgent())
    }

    /**
     * Kinda shitty device.. verify if it's a Samsung stuff.. so many bugs - worth a getter.
     *
     * @returns {boolean} - Returns true if the device is from the Samsung family.
     */
    public isSamsung(): boolean {
        return (/SAMSUNG|GT-I|SCH-|GT-N|GT-P|SCH-|SM-N|SGH-/i).test(this.getUserAgent());
    }

    /**
     * Is IphoneX
     *
     * @returns {boolean} - Returns true if the device is IphoneX
     */
    public isIphoneX(): boolean {
        return 10 === this.getIphoneVersion();
    }

    /**
     * Getting the current running iPhone device version, hardware version.
     * Able to detect iPhone 4 and 5 and iphone x (10)
     *
     * @returns {*} - Returns NULL if device is not an iPhone. Will return a Number of the hardware version.
     */
    public getIphoneVersion(): number {

        if (11 <= this.getIOSVersion() &&
            (
                //iphone 12 pro max
                (926 === window.screen.height && 428 === window.screen.width) || (926 === window.screen.width && 428 === window.screen.height)
                ||
                //iphone 12 pro
                (844 === window.screen.height && 390 === window.screen.width) || (844 === window.screen.width && 390 === window.screen.height)
                ||
                // iPhone X / Xs
                (812 === window.screen.height && 375 === window.screen.width) || (812 === window.screen.width && 375 === window.screen.height)
                ||
                // iPhone Xr / Xs Max
                (896 === window.screen.height && 414 === window.screen.width) || (896 === window.screen.width && 414 === window.screen.height)
            )
        ) {
            return 10;
        }

        if (8 <= this.getIOSVersion() && 375 === window.screen.width) {
            return 6;
        }

        if (6 <= this.getIOSVersion() && 320 === window.screen.width) {
            return 5;
        }

        // iphone 4/s (Retina)
        if (5 <= this.getIOSVersion() && 640 === window.screen.width) {
            return 4;
        }

        return 0;
    }

    /**
     *
     * @returns {DeviceConst.DeviceType}
     * @public
     */
    public getDeviceSystemData(): DeviceConst.DeviceType {
        return this._getDeviceType();
    }

    /**
     *
     *
     * @returns {boolean}
     */
    public isSupportHTML5(): boolean {
        return 'function' === typeof document.createElement('canvas').getContext;
    }


    /**
     * Check whether the browser has cookie enable 3rd party.
     *
     * @returns {boolean}
     */
    public isCookieEnable(): boolean {
        return navigator.cookieEnabled;
    }


    /**
     * Get the pixel ratio for the current running device.
     *
     * @returns {number}
     */
    public getPixelRatio(): number {
        var ratio = 1;

        const screen = window.screen as any;

        // To account for zoom, change to use deviceXDPI instead of systemXDPI
        if (screen.systemXDPI !== undefined && screen.logicalXDPI !== undefined && screen.systemXDPI > screen.logicalXDPI) {
            // Only allow for values > 1
            ratio = screen.systemXDPI / screen.logicalXDPI;
        } else if (window.devicePixelRatio !== undefined) {
            ratio = window.devicePixelRatio;
        }
        return ratio;
    }


    /**
     * Get screen height.
     * @returns {Number}
     */
    public getScreenHeight(): number {
        return window.screen.height;
    }


    /**
     * Get screen width.
     * @returns {Number}
     */
    public getScreenWidth(): number {
        return window.screen.width;
    }


    /**
     * Get min width for PC.
     *
     * @returns {number}
     */
    public getMinWidthForPC(): number {
        return this._minWidthForPC;
    }


    /**
     * Get benchmark.
     *
     * @returns {number}
     */
    public getBenchMark(): number {
        var b = { m: 0 }, bd = [], bmt = + new Date() + 50;
        do {
            var t, count = 0, tt = + new Date();
            do { t = + new Date(); } while (t === tt);
            do { count++; tt = +new Date(); } while (t === tt);
            if ((tt - t) === 1) { bd[bd.length] = count; }
        } while ((bd.length < 20) && (t < bmt));

        if (bd.length === 0) b.m = 0; else b.m = Math.max.apply(Math, bd);

        return b.m;
    }

    /***
     * is unsupported Browser?
     */
    public isUnsupportedBrowser(): boolean {
        if (this.isHybrid()) {
            return false;
        }

        if (this.isSamsungInternet()) {
            return true;
        }

        return false;
    }

    public isMacintosh(): boolean {
        const deviceData = this.getDeviceSystemData();
        if (deviceData) {
            return deviceData.operationSystem === 'Macintosh';
        }
        return false;
    }

    matchMedia(media: string) {
        return window.matchMedia(media).matches;
    }

    isProgressiveWebApp(): boolean {
		// PWA setting is "display": "fullscreen"
		// but installed on desktop it's detected as "standalone" if app is not in fullscreen mode or "fullscreen" if user enters fullscreen mode
        // iOS is identified through check the navigator to have attribute 'standalone'

		return this.isStandalone() ||
            this.matchMedia('(display-mode: fullscreen)') ||
            this.matchMedia('(display-mode: standalone)');

    }

    /**
     * get the class Name that injected iside the hybrid
     *
     * @returns String
     */
    private _getClassName(name: string, defaultName: string): string {
        const className = undefined !== name ? name : defaultName;

        return className;
    }

    /**
     *
     * @returns {{}}
     * @private
     */
    private _getDeviceType(): DeviceConst.DeviceType {
        const deviceType: DeviceConst.DeviceType = {} as DeviceConst.DeviceType;
        deviceType.isHybrid = false;
        deviceType.isEmbeddedHybrid = false;
        deviceType.isHybridPoker = false;

        try {
            if (this.isPC()) {
                deviceType.deviceType = 'pc';
                deviceType.operationSystem = this._getPCOperatingSystem();
            }

            if (this.isMobile()) {
                deviceType.deviceType = 'mobile';
                // condition added so that operationSystem is populated when simulating mobile on PC
                deviceType.operationSystem = this._getMobileOperationSystem() || this._getPCOperatingSystem();
            }

            if (this.isTablet()) {
                deviceType.tool = 'tablet';
            }

            if (this.isHybrid()) {
                deviceType.isHybrid = true;
            }

            if (this.isHybridEmbedded()) {
                deviceType.isEmbeddedHybrid = true;
                deviceType.isHybridPoker = this.isPokerHybrid();
            }

            return deviceType;
        } catch (e) {
            deviceType.deviceType = 'pc';
            deviceType.operationSystem = 'windows';

            this.logger.error('Error in _getDeviceType, Initialize with default params{deviceType = pc,operationSystem =windows}. error message: ', e);
            return deviceType;
        }
    }

    /**
     * Returns the PC ( non-mobile ) operating system
     * @returns {*} string or undefined
     * @private
     */
    private _getPCOperatingSystem(): string {
        try {
            return (/windows|linux|MacOS|Macintosh|UNIX|Ubuntu/i).exec(navigator.appVersion)[0];
        } catch (e) {
            try {
                this.logger.error('Error in _getPCOperatingSystem, Initialize with default params{OperatingSystem = windows}. error message: ', e, 'navigator.appVersion value: ', navigator.appVersion);
            } catch (e) {
                this.logger.error('Error in _getPCOperatingSystem, Initialize with default params{OperatingSystem = windows}. error message: ', e, ' Failed to get navigator.appVersion value');
            }

            return 'windows';
        }
    }


    /**
     * Get the device operation system.
     *
     *
     * @returns {*}
     * @private
     */
    private _getMobileOperationSystem(): string {
        try {
            if (this.isAndroid()) {
                return 'android';
            }

            if (this.isIOS()) {
                return 'ios';
            }

            return 'android';
        } catch (e) {
            this.logger.error('Error in _getMobileOperationSystem, Initialize with default params{OperatingSystem = android}. error message: ', e);
            return 'android'
        }
    }

    get browserName(): string {
        if(this.isEdge()) {
            return 'Edge';
        } else if(this.isChrome()) {
            return 'Chrome';
        } else if(this.isSafari()) {
            return 'Safari';
        } else if(this.isFirefox()) {
            return 'Firefox';
        } else {
            return 'Unknown'
        }
    }

    isMobileWebApp() {
        return (this.isMobileByOS() || this.isIpadRealDevice()) && !this.isTypeOfHybrid();
    }
}

export default new Device();
